import { Popover } from '@abyss/web/ui/Popover';
import find from 'lodash/find';
import { useEffect, useState } from 'react';

import { adobeModalTrackEvent } from '../../../common/AdobeTagging/adobeModalTrackEvent';
import { ConstantsLagoon } from '../../../common/ConstantsLagoon';
import { getFeatureFlag } from '../../../common/Utils';
import { useLagoon } from '../../../hooks/useLagoon';
import { useSmartChoice } from '../../../hooks/useSmartChoice';
import { useIsEligibleForStandaloneDental } from '../../../utils/user.utils';
import { adobeLinkTrackEvent } from '../../AdobeTagging/adobeLinkTrackEvent';
import {
  StyledPopoverWrapper,
  StyledSmartChoiceChipContainer,
  StyledSmartChoiceChipText,
  StyledSmartChoiceWrapper,
} from './SmartChoiceInfoChip.styled';

interface SmartChoiceInfoChipProps {
  chipLabel?: string;
  popoverTitle?: string;
  popoverContent?: string;
  wrapperStyle?: any;
  category?: string;
}

export const SmartChoiceInfoChip = ({
  chipLabel = '',
  popoverTitle = '',
  popoverContent = '',
  wrapperStyle = {},
  category: _category = '',
}: SmartChoiceInfoChipProps) => {
  const lagoonData = useLagoon('ui-messaging')();
  const smartChoiceEligibility = useSmartChoice();

  const featureFlags: [{ key: string; active: boolean }] =
    useLagoon('feature-flags')();

  const smartChoiceNotAvailable = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE.SMART_CHOICE_NOT_AVAILABLE,
  });
  const smartChoiceAvailability = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE.SMART_CHOICE_AVAILABILITY,
  });

  // Disclaimer feature flag (component-specific, not SmartChoice core logic)
  const enableNonSmartChoiceDisclaimer = getFeatureFlag(
    featureFlags,
    ConstantsLagoon?.FEATURE_FLAGS.ENABLE_NON_SMART_CHOICE_DISCLAIMER
  );

  // Analytics feature flag (component-specific)
  const enableAdobeAnalytic = getFeatureFlag(
    featureFlags,
    ConstantsLagoon?.FEATURE_FLAGS.ENABLE_NEW_UX_SMART_CHOICE_ANALYTICS
  );
  const isEligibleForStandaloneDental = useIsEligibleForStandaloneDental();

  const renderSCChip =
    enableNonSmartChoiceDisclaimer &&
    !isEligibleForStandaloneDental &&
    (smartChoiceEligibility.flags.enablePSXProviderRecommendations ||
      smartChoiceEligibility.flags.enablePSXFacilitiesRecommendations) &&
    !smartChoiceEligibility.flags.isSmartChoiceSuppressed &&
    !smartChoiceEligibility.isEnabled;

  const [openInfoPopover, setOpenInfoPopover] = useState(false);
  const chipTxt = chipLabel ? chipLabel : smartChoiceNotAvailable?.message;
  const popoverTxt = popoverTitle
    ? popoverTitle
    : smartChoiceAvailability?.message;

  useEffect(() => {
    if (!openInfoPopover || !popoverTxt || !enableAdobeAnalytic) return;
    adobeModalTrackEvent(popoverTxt);
  }, [openInfoPopover]);

  const handleInfoClick = () => {
    if (openInfoPopover) return;
    if (!enableAdobeAnalytic) return;
    adobeLinkTrackEvent({
      name: `${chipTxt} icon`,
      location: chipTxt,
    });
  };

  return (
    renderSCChip && (
      <StyledSmartChoiceWrapper
        css={wrapperStyle}
        data-testid="smart-choice-wrapper"
      >
        <StyledSmartChoiceChipContainer data-testid="smart-choice-chip-container">
          <StyledSmartChoiceChipText data-testid="smart-choice-chip-text">
            {chipTxt}
          </StyledSmartChoiceChipText>
          <StyledPopoverWrapper data-testid="smart-choice-popover-wrapper">
            <Popover
              content={popoverContent}
              data-testid="smart-choice-popover"
              onClick={handleInfoClick}
              onOpenChange={() => {
                setOpenInfoPopover(!openInfoPopover);
              }}
              open={openInfoPopover}
              title={popoverTxt}
            />
          </StyledPopoverWrapper>
        </StyledSmartChoiceChipContainer>
      </StyledSmartChoiceWrapper>
    )
  );
};
